import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BiMessageSquareDetail, BiMessageSquareAdd, BiX, BiUpvote, BiDownvote } from 'react-icons/bi';
import { usePostgresUser } from '../utils/PostgresUserContext';
import LeaderboardMessageModal from '../modals/LeaderboardMessageModal';
import { backendService } from '../../services/api/backendService';
import { getToken } from '../../config/firebase';

/**
 * Smart leaderboard row message component that shows different UI based on user context
 * - If current user's row: "Click here to set leaderboard message"
 * - If other user's row with message: "Click to see leaderboard message" (Elden Ring style)
 * - If other user's row without message: Nothing shown
 * 
 * @param {Object} leaderboardEntry - The leaderboard entry data
 * @param {string} leaderboardEntry.publicUsername - Public username of the leaderboard entry
 * @param {Object|null} leaderboardEntry.leaderboardMessage - Message data from backend
 * @param {number} position - Position on the leaderboard (for styling)
 */
const LeaderboardRowMessage = ({ leaderboardEntry, position }) => {
  const { user } = usePostgresUser();
  const [showModal, setShowModal] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [messageVotes, setMessageVotes] = useState({ upvotes: 0, downvotes: 0 });
  const [isVoting, setIsVoting] = useState(false);
  const [hasVotedThisSession, setHasVotedThisSession] = useState(false);
  const previousOverflow = useRef();

  // Body scroll locking effect for message popup
  useEffect(() => {
    if (showMessagePopup) {
      // Store current overflow style
      previousOverflow.current = document.body.style.overflow;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Add padding to prevent layout shift on mobile
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore previous overflow
        document.body.style.overflow = previousOverflow.current || '';
        document.body.style.paddingRight = '';
      };
    }
  }, [showMessagePopup]);

  // Initialize vote counts when message data changes
  useEffect(() => {
    if (leaderboardEntry.leaderboardMessage) {
      setMessageVotes({
        upvotes: leaderboardEntry.leaderboardMessage.upvotes || 0,
        downvotes: leaderboardEntry.leaderboardMessage.downvotes || 0
      });
      // Reset voting session state when message data changes
      setHasVotedThisSession(false);
    }
  }, [leaderboardEntry.leaderboardMessage]);

  // Vote on message function
  const handleVote = async (voteType) => {
    if (!user || isVoting || !leaderboardEntry.leaderboardMessage || hasVotedThisSession) return;
    
    setIsVoting(true);
    try {
      // Get auth token
      const idToken = getToken();
      if (!idToken) {
        console.error('No authenticated user found');
        return;
      }
      
      const response = await backendService.voteOnLeaderboardMessage(
        leaderboardEntry.publicUsername, 
        voteType,
        idToken
      );
      
      if (response.success) {
        setMessageVotes({
          upvotes: response.data.upvotes,
          downvotes: response.data.downvotes
        });
        setHasVotedThisSession(true);
      }
    } catch (error) {
      console.error('Error voting on message:', error);
    } finally {
      setIsVoting(false);
    }
  };

  // Don't render anything if no user is logged in
  if (!user || !user.publicUsername) {
    return null;
  }

  const isCurrentUser = user.publicUsername === leaderboardEntry.publicUsername;
  const hasMessage = leaderboardEntry.leaderboardMessage?.message;
  const messageData = leaderboardEntry.leaderboardMessage;

  // Current user's row - show message management
  if (isCurrentUser) {
    return (
      <>
        <div className="flex items-center justify-center">
          <button
            onClick={() => setShowModal(true)}
            className={`
              flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105
              ${hasMessage 
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-300' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300 hover:border-orange-300'
              }
            `}
            title={hasMessage ? 'Click to edit your leaderboard message' : 'Click to set your leaderboard message'}
          >
            {hasMessage ? (
              <>
                <BiMessageSquareDetail className="text-sm" />
                <span>Edit Message</span>
              </>
            ) : (
              <>
                <BiMessageSquareAdd className="text-sm" />
                <span>Set Message</span>
              </>
            )}
          </button>
        </div>

        {/* Modal for message creation/editing */}
        <LeaderboardMessageModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          mode={hasMessage ? 'edit' : 'create'}
        />
      </>
    );
  }

  // Other user's row with message - show Elden Ring style reveal
  if (hasMessage) {
    return (
      <>
        <div className="flex items-center justify-center">
          <button
            onClick={() => setShowMessagePopup(true)}
            className="flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300 transition-all duration-200 hover:scale-105"
            title="Click to see leaderboard message"
          >
            <BiMessageSquareDetail className="text-sm" />
            <span>See Message</span>
          </button>
        </div>

        {/* Elden Ring style message popup with portal */}
        {showMessagePopup && createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4"
            onClick={() => setShowMessagePopup(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
          >
            <div 
              className="relative bg-gray-900 border-2 border-gray-600 rounded-lg shadow-2xl w-80 max-w-[90vw] p-6 text-center"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxHeight: '80vh'
              }}
            >
              {/* Close button */}
              <button
                onClick={() => setShowMessagePopup(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-200 transition-colors"
              >
                <BiX className="text-xl" />
              </button>

              {/* Message content - Elden Ring style */}
              <div className="mb-4">
                <div className="text-gray-400 text-sm mb-2">Message from</div>
                <div className="text-orange-400 font-semibold text-lg mb-4">
                  {leaderboardEntry.publicUsername}
                </div>
                
                {/* The actual message in a parchment-style box */}
                <div className="bg-yellow-50 border-2 border-yellow-600 rounded-lg p-4 shadow-inner">
                  <div className="text-gray-800 font-medium text-lg italic">
                    "{messageData.message}"
                  </div>
                </div>
                
                {/* Message timestamp */}
                {messageData.createdAt && (
                  <div className="text-gray-500 text-xs mt-3">
                    Left on {new Date(messageData.createdAt).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Voting buttons */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => handleVote('up')}
                  disabled={isVoting || isCurrentUser || hasVotedThisSession}
                  className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isCurrentUser || hasVotedThisSession
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                  title={
                    isCurrentUser 
                      ? "Can't vote on your own message" 
                      : hasVotedThisSession 
                      ? "Already voted this session" 
                      : 'Upvote this message'
                  }
                >
                  <BiUpvote className="text-sm" />
                  <span>{messageVotes.upvotes}</span>
                </button>
                
                <button
                  onClick={() => handleVote('down')}
                  disabled={isVoting || isCurrentUser || hasVotedThisSession}
                  className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isCurrentUser || hasVotedThisSession
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                  title={
                    isCurrentUser 
                      ? "Can't vote on your own message" 
                      : hasVotedThisSession 
                      ? "Already voted this session" 
                      : 'Downvote this message'
                  }
                >
                  <BiDownvote className="text-sm" />
                  <span>{messageVotes.downvotes}</span>
                </button>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowMessagePopup(false)}
                className="bg-logoColor hover:bg-logoColor/90 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl border border-gray-200"
              >
                Close
              </button>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // Other user's row without message - return empty div to maintain grid alignment
  return <div className="w-full" />;
};

export default LeaderboardRowMessage;